'use client';
import { useCustomerWishlistSkus } from '@/common/hooks/useCustomerWishlistSkus/useCustomerWishlistSkus';
import { useGaViewItem } from '@/components/ProductFullDetail-new/hooks/use-ga-view-item';
import SuggestedProducts from '@/components/SuggestedProducts/suggestedProducts';
import { CURRENT_STORE_COUNTRY_NAME } from '@/constants/current-store-country-name';
import { SEO_CATEGORY_MAP } from '@/constants/seoCategoryMap';
import { websiteEnv } from '@/constants/website-env';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import LazyLoad from 'react-lazyload';
import HrefLangLinks from '../href-lang-links';
import { ProductFullDetailMask } from '../product-full-detail-mask';
import { ProductRating } from '../ProductFullDetail-new/components/product-rating';
import { ProductTitle } from '../ProductFullDetail-new/components/product-title';
import { WishlistButton } from '../ProductFullDetail-new/components/wishlist';
import type { RefType } from '../ProductFullDetail-new/productFullDetail';
import { ProductFullDetailNew } from '../ProductFullDetail-new/productFullDetail';
import { useGetCurrentFabricShowTabs } from './use-get-current-fabric-show-tabs';

const ProductImageGallery = dynamic(() => import('@/components/ProductImageGallery'), { ssr: false });
const VideoGallery = dynamic(() => import('../video-gallery/video-gallery'), { ssr: false });
const RatingsAndQuestions = dynamic(() => import('@/components/RatingsAndQuestions'), { ssr: false });

const Product = ({
  urlKey,
  seoTitle,
  seoDescription,
  defaultColorLabel,
  product,
  breadCrumb,
  categoryName,
  searchParamsRecord,
  productAccordion,
  upsellProductSlider,
  relatedProductsDefaultFilterColor,
  defaultRelatedProductSlider,
}: {
  urlKey: string;
  seoTitle: string;
  seoDescription: string;
  defaultColorLabel: string;
  categoryName: string;
  product: any;
  breadCrumb: ReactNode;
  productAccordion: ReactNode;
  searchParamsRecord: Record<string, string | string[] | undefined>;
  upsellProductSlider: ReactNode;
  relatedProductsDefaultFilterColor: string;
  defaultRelatedProductSlider;
}) => {
  useCustomerWishlistSkus();
  const [reviewMaskOpen, setReviewMaskOpen] = useState(false);
  const pathname = usePathname();
  const isSecond = searchParamsRecord['in-stock'] === 'true';
  const initalProduct = isSecond ? 'available_product' : 'product';
  const [currentProduct, setCurrentProduct] = useState<'product' | 'available_product'>(initalProduct);
  const placeholderRef = useRef(null);
  const [seoColorLabel, setSeoColorLabel] = useState(defaultColorLabel);
  const availableProductRef = useRef(null);
  const [relatedProductFilterColor, setRelatedProductFilterColor] = useState(relatedProductsDefaultFilterColor);
  const [hrefLangProductUrl, setHrefLangProductUrl] = useState(pathname);

  const { version } = product || {};
  const isInSeoCategory = product?.categories?.some(
    (x: any) => SEO_CATEGORY_MAP[x?.uid as unknown as keyof typeof SEO_CATEGORY_MAP],
  );
  const hasAvailableProduct = product?.available_product;

  const onViewReviews = useCallback(() => {
    placeholderRef.current &&
      (placeholderRef.current as any).scrollIntoView({
        behavior: 'smooth',
      });
  }, [placeholderRef]);

  const handleRefreshUrlKey = (colorLabel: string) => {
    if (!isInSeoCategory || colorLabel === seoColorLabel) {
      return;
    }

    let nextPathname = pathname;

    if (colorLabel) {
      const productUrl = `/${urlKey.replace(
        /^([^-]*-[^-]*)/,
        `$1-${colorLabel.trim().toLowerCase().replace(/\s/g, '-')}`,
      )}.html`;
      nextPathname = `${process.env.NEXT_PUBLIC_BASE_PATH}${productUrl}`;
      setHrefLangProductUrl(productUrl);
    } else {
      nextPathname = `${process.env.NEXT_PUBLIC_BASE_PATH}/${urlKey}.html`;
      setHrefLangProductUrl(`/${urlKey}.html`);
    }

    window.history.replaceState(
      {
        isProductOption: true,
      },
      '',
      nextPathname,
    );
  };

  const handleColorLabelChange = (colorLabel: string) => {
    setSeoColorLabel(colorLabel);
    handleRefreshUrlKey(colorLabel);
    handleRefreshSeoInfo(colorLabel, categoryName);
  };

  const handleProductTypeChange = (nextProductType: 'product' | 'available_product') => {
    setCurrentProduct(nextProductType);

    if (nextProductType === 'available_product') {
      handleRefreshUrlKey('');
      setSeoColorLabel('');
      handleRefreshSeoInfo('', categoryName);

      return;
    }

    const nextFabricLabel = productFullDetailNewRef.current?.form?.getValues('Fabric')?.label || '';
    const isNotDefaultFabric = nextFabricLabel && nextFabricLabel.slice(-8) !== '-default';
    const nextColorLabel = isNotDefaultFabric
      ? ''
      : productFullDetailNewRef.current?.form?.getValues('Color')?.label || '';

    setSeoColorLabel(nextColorLabel);
    handleRefreshSeoInfo(nextColorLabel, categoryName);
    handleRefreshUrlKey(nextColorLabel);
  };

  const handleRefreshSeoInfo = useCallback(
    (colorLabel: string, categoryName: string) => {
      if (!isInSeoCategory) {
        return;
      }

      const metaElement = document.querySelector('meta[name="description"]');
      const canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;

      if (!colorLabel) {
        document.title = seoTitle;
        metaElement && metaElement.setAttribute('content', seoDescription);
        canonicalLink &&
          (canonicalLink.href = window.location.origin + process.env.NEXT_PUBLIC_BASE_PATH + `/${urlKey}.html`);

        return;
      }

      if (canonicalLink) {
        canonicalLink.href =
          window.location.origin +
          process.env.NEXT_PUBLIC_BASE_PATH +
          `/${urlKey.replace(/^([^-]*-[^-]*)/, `$1-${colorLabel.trim().toLowerCase().replace(/\s/g, '-')}`)}.html`;
      }

      const capitalizedColorLabel = colorLabel
        .trim()
        .split(' ')
        .map(x => {
          return `${x[0].toUpperCase()}${x.slice(1).toLowerCase()}`;
        })
        .join(' ');

      if (seoTitle) {
        const titleParts = seoTitle.split(' ').reduce<string[]>((acc, cur) => {
          if (!cur) {
            return acc;
          }

          const word = cur.trim();

          if (!word) {
            return acc;
          }

          acc.push(word);

          return acc;
        }, []);

        document.title = [
          'AW',
          titleParts[1],
          capitalizedColorLabel,
          categoryName,
          websiteEnv.isUnitedStates
            ? '| AW.Bridal'
            : `| AW.Bridal ${websiteEnv.isUnitedKingdom ? 'UK' : CURRENT_STORE_COUNTRY_NAME}`,
        ]
          .filter(Boolean)
          .join(' ');
      }

      if (seoDescription && metaElement) {
        metaElement.setAttribute('content', `${seoDescription} Available in ${colorLabel.toLowerCase()}.`);
      }
    },
    [isInSeoCategory, seoDescription, seoTitle, urlKey],
  );

  useEffect(() => {
    if (defaultColorLabel) {
      handleRefreshSeoInfo(defaultColorLabel, categoryName);
    }
  }, [defaultColorLabel, categoryName, handleRefreshSeoInfo]);

  const productFullDetailNewRef = useRef<RefType>(null);

  const { currentFabricArrayShowTabs, buttonThatDoNotExistFabricArray } = useGetCurrentFabricShowTabs({
    product,
  });

  useGaViewItem({
    product,
  });

  return (
    <div className='bg-[#f9f9f9]  lg_bg-white '>
      <HrefLangLinks
        isProductPage
        isConfigurableProduct={product.__typename === 'ConfigurableProduct'}
        productCategories={product.categories}
        productUrl={hrefLangProductUrl}
      />
      <ProductFullDetailMask
        open={reviewMaskOpen}
        onOpenChange={setReviewMaskOpen}
      />

      {breadCrumb}

      <ProductFullDetailNew
        product={product}
        onViewReviews={onViewReviews}
        urlKey={urlKey}
        setCurrentProduct={handleProductTypeChange}
        className={cn(currentProduct === 'product' ? '' : 'hidden')}
        currentProduct={currentProduct}
        hasAvailableProduct={hasAvailableProduct}
        productTitle={
          <ProductTitle
            seoColorLabel={seoColorLabel}
            name={product?.name}
          />
        }
        productRating={
          <ProductRating
            product={product}
            onViewReviews={onViewReviews}
          />
        }
        productFullAccordion={productAccordion}
        wishlistDom={
          <WishlistButton
            classes={{
              root: clsx(
                'text-[15px] flex-none leading-5 underline flex items-center gap-x-[5px] justify-center mx-auto',
              ),
              root_selected: 'text-[15px] leading-5 underline',
            }}
            product={product}
            selectedOptionsArray={[]}
          />
        }
        currentFabricArrayShowTabs={currentFabricArrayShowTabs}
        categoryUrlKey={product?.categories?.[0]?.url_key || ''}
        ref={productFullDetailNewRef}
        show_attribute={product?.show_attribute}
        outerProduct={product}
        categoryName={categoryName}
        onColorLabelChange={handleColorLabelChange}
        defaultColorLabel={defaultColorLabel}
        searchParamsRecord={searchParamsRecord}
        upsellProductSlider={upsellProductSlider}
        setRelatedProductFilterColor={setRelatedProductFilterColor}
      />

      {hasAvailableProduct && (
        <ProductFullDetailNew
          setRelatedProductFilterColor={setRelatedProductFilterColor}
          searchParamsRecord={searchParamsRecord}
          product={product?.available_product}
          onViewReviews={onViewReviews}
          urlKey={urlKey}
          setCurrentProduct={handleProductTypeChange}
          className={cn(currentProduct === 'available_product' ? '' : 'hidden')}
          instockSku={product?.sku}
          currentProduct={currentProduct}
          ref={availableProductRef}
          hasAvailableProduct={hasAvailableProduct}
          productTitle={
            <ProductTitle
              seoColorLabel={seoColorLabel}
              name={product?.name}
            />
          }
          productRating={
            <ProductRating
              product={product}
              onViewReviews={onViewReviews}
            />
          }
          productFullAccordion={productAccordion}
          wishlistDom={
            <WishlistButton
              classes={{
                root: clsx(
                  'text-[15px] flex-none leading-5 underline flex items-center gap-x-[5px] justify-center mx-auto',
                ),
                root_selected: 'text-[15px] leading-5 underline',
              }}
              product={product}
              selectedOptionsArray={[]}
            />
          }
          shouldSelectFirstColor
          categoryUrlKey={product?.categories?.[0]?.url_key || ''}
          createFabricButton={
            <CreateFabricButton
              buttonThatDoNotExistFabricArray={buttonThatDoNotExistFabricArray}
              onClick={item => {
                setCurrentProduct('product');

                // todo 切换 fabric
                productFullDetailNewRef.current?.handleFabricChange?.(item);
              }}
            />
          }
          show_attribute={product?.show_attribute}
          outerProduct={product}
          categoryName={categoryName}
          upsellProductSlider={upsellProductSlider}
        />
      )}

      <SuggestedProducts
        urlKey={urlKey}
        upsellProductSlider={upsellProductSlider}
        relatedProductFilterColor={relatedProductFilterColor}
        defaultRelatedProductSlider={defaultRelatedProductSlider}
        relatedProductsDefaultFilterColor={relatedProductsDefaultFilterColor}
      />
      <LazyLoad>
        <ProductImageGallery
          product={product}
          version={version}
        />
      </LazyLoad>
      <LazyLoad>
        <VideoGallery urls={generateVideoGalleryUrls(product.video_gallery)} />
      </LazyLoad>
      <div ref={placeholderRef} />
      <LazyLoad>
        <RatingsAndQuestions
          urlKey={urlKey}
          productId={product.id}
          sku={product.sku}
          questionAndAnswers={product?.question_answer}
          placeholderRef={placeholderRef}
          reviewMaskOpen={reviewMaskOpen}
          setReviewMaskOpen={setReviewMaskOpen}
          isWeddingShoes={product?.categories?.[0]?.uid === 'MTYy'}
        />
      </LazyLoad>
    </div>
  );
};

const generateVideoGalleryUrls = (video_gallery: string) => {
  if (!video_gallery) {
    return [];
  }

  return video_gallery.split(/\r\n?|\n/).reduce<string[]>((acc, cur) => {
    if (cur) {
      acc.push(cur);
    }

    return acc;
  }, []);
};

const CreateFabricButton = props => {
  const { buttonThatDoNotExistFabricArray, onClick } = props;

  if (!Array.isArray(buttonThatDoNotExistFabricArray) || buttonThatDoNotExistFabricArray.length === 0) {
    return null;
  }

  return (
    <>
      {buttonThatDoNotExistFabricArray.map((item: any) => {
        return (
          <button
            key={item}
            type='button'
            className='tile_root__gozBD flex min-h-[36px] min-w-[157px] cursor-pointer items-center justify-center border border-solid border-bgtheme bg-[#f8f3ef] px-1 text-sm leading-none hover_border hover_border-solid hover_border-primary peer-disabled_cursor-not-allowed peer-disabled_opacity-70 peer-data-[state=checked]_bg-primary peer-data-[state=checked]_text-white lg_text-[16px] [&:has([data-state=checked])]_border-primary [&:has([data-state=checked])]_text-white'
            onClick={() => {
              onClick(item);
            }}
          >
            {item}
          </button>
        );
      })}
    </>
  );
};

export default Product;
